import React, { useCallback, useEffect, useState } from "react";
import { LuRefreshCw, LuSend, LuX, LuUpload, LuListChecks, LuImage, LuTrash2 } from "react-icons/lu";
import { FiArrowLeft, FiVideo, FiPhoneCall, FiMoreVertical, FiChevronRight } from "react-icons/fi";
import { authFetch, buildQuery } from "../../utils/api";
import { parseUtc, formatDateTime, formatFull } from "../../utils/datetime";
import "../leadFinder/LeadFinder.css";
import "./WhatsApp.css";

/** "Today" / "3d ago" / "2mo ago" for the last-messaged column. */
function relativeDay(value) {
  const then = parseUtc(value);
  if (!then) return "—";
  const days = Math.floor((Date.now() - then.getTime()) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function statusTone(status) {
  const s = String(status || "").toLowerCase();
  if (s === "approved" || s === "completed" || s === "delivered" || s === "read" || s === "sent") return "lf-badge-green";
  if (s === "failed" || s === "rejected" || s === "cancelled") return "lf-badge-gray";
  if (s === "running" || s === "queued" || s === "pending") return "lf-badge-blue";
  return "lf-badge-gray";
}

const WA_TABS = new Set(["campaign", "direct", "templates", "logs"]);

function whatsappTabFromHash() {
  const parts = String(window.location.hash || "").replace(/^#\/?/, "").split("/");
  const tab = (parts[1] || "campaign").toLowerCase();
  return WA_TABS.has(tab) ? tab : "campaign";
}

function setWhatsAppHash(tab) {
  const next = `#/whatsapp/${tab}`;
  if (window.location.hash !== next) {
    window.history.replaceState(null, "", next);
  }
}

/**
 * Only the problem state gets a banner. A healthy connection is ambient
 * information, so it reads as the small "Connected" pill in the header
 * (see ConnectedPill) rather than a full-width bar the user must scan past
 * on every visit.
 */
function ConfigBanner({ status }) {
  if (!status) return null;
  if (status.configured) return null;
  return (
    <div className="wa-banner wa-banner-warn">
      WhatsApp is not configured. Set <code>WA_PHONE_NUMBER_ID</code>, <code>WA_WABA_ID</code>,{" "}
      <code>WA_ACCESS_TOKEN</code>, <code>META_APP_SECRET</code>, and <code>WEBHOOK_VERIFY_TOKEN</code> in{" "}
      <code>backend/.env</code>, then restart the server.
    </div>
  );
}

/** Green "Connected" pill shown at the end of the page header. */
function ConnectedPill({ status }) {
  if (!status?.configured) return null;
  return (
    <span className="wa-connected-pill" title="WhatsApp Cloud API configured (WABA ready)">
      <span className="wa-connected-dot" aria-hidden />
      Connected
    </span>
  );
}

function TemplatesTab({ notify }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch("/whatsapp/templates");
      setTemplates(res.templates || []);
    } catch (err) {
      notify(err.message || "Failed to load templates", "error");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await authFetch("/whatsapp/templates/sync", { method: "POST" });
      setTemplates(res.templates || []);
      notify(`Synced ${res.synced || 0} template(s) from Meta`, "success");
    } catch (err) {
      notify(err.message || "Sync failed", "error");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="lf-card">
      <div className="lf-progress-head">
        <h3 className="lf-card-title" style={{ margin: 0 }}>Message Templates</h3>
        <button className="lf-btn lf-btn-primary lf-btn-sm" onClick={sync} disabled={syncing}>
          <LuRefreshCw style={{ verticalAlign: "-2px", marginRight: 6 }} />
          {syncing ? "Syncing…" : "Sync from Meta"}
        </button>
      </div>
      <p className="lf-subtitle" style={{ marginBottom: 16 }}>
        Create and approve MARKETING templates in Meta Business Manager, then sync here.
      </p>

      {loading ? (
        Array.from({ length: 4 }).map((_, i) => <div key={i} className="lf-skeleton" />)
      ) : templates.length === 0 ? (
        <div className="lf-empty">No templates yet. Sync from Meta after approving templates.</div>
      ) : (
        <div className="lf-table-wrap">
          <table className="lf-table">
            <thead>
              <tr>
                <th className="no-sort">Name</th>
                <th className="no-sort">Language</th>
                <th className="no-sort">Category</th>
                <th className="no-sort">Status</th>
                <th className="no-sort">Body</th>
                <th className="no-sort">Synced</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => (
                <tr key={`${t.name}-${t.language}`}>
                  <td className="lf-username">{t.name}</td>
                  <td>{t.language}</td>
                  <td>{t.category || "—"}</td>
                  <td><span className={`lf-badge ${statusTone(t.status)}`}>{t.status}</span></td>
                  <td className="wa-body-cell">{t.body_text || "—"}</td>
                  <td className="lf-muted wa-log-time">{formatDateTime(t.synced_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CampaignTab({ notify, onCreated, templates = [] }) {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [keyword, setKeyword] = useState("");
  const [location, setLocation] = useState("");
  // Opens on 'new' so the default view is leads not yet attempted; 'contacted'
  // holds everything already tried, failures included.
  const [contactState, setContactState] = useState("new");
  const [deleting, setDeleting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [templateKey, setTemplateKey] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [bodyParam, setBodyParam] = useState("");
  const [headerImageFile, setHeaderImageFile] = useState(null);
  const [headerImageUrl, setHeaderImageUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  // Fixed until the Android/iPhone toggle returns; still selects frame styling.
  const [device] = useState("android");

  const selectedTemplate = templates.find((t) => `${t.name}::${t.language}` === templateKey);
  const needsHeaderImage = String(selectedTemplate?.header_type || "").toUpperCase() === "IMAGE";

  // Switching to a template that no longer needs an image (or away from one
  // that did) must not silently carry a stale file into the next send.
  useEffect(() => {
    if (!needsHeaderImage) setHeaderImageFile(null);
  }, [needsHeaderImage]);

  // Object URLs leak unless revoked, and the image may be swapped repeatedly.
  useEffect(() => {
    if (!headerImageFile) { setHeaderImageUrl(""); return undefined; }
    const url = URL.createObjectURL(headerImageFile);
    setHeaderImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [headerImageFile]);

  // `templates` is fetched once by the parent (approved MARKETING only —
  // UTILITY and AUTHENTICATION are transactional and Meta rejects them for
  // promotional sends). This only picks the default once they arrive.
  useEffect(() => {
    if (templates.length && !templateKey) {
      setTemplateKey(`${templates[0].name}::${templates[0].language}`);
    }
  }, [templates, templateKey]);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`/whatsapp/contacts${buildQuery({ page, pageSize: 25, q, keyword, location, contactState })}`);
      setContacts(res.rows || []);
      setTotal(res.total || 0);
    } catch (err) {
      notify(err.message || "Failed to load contacts", "error");
    } finally {
      setLoading(false);
    }
  }, [page, q, keyword, location, contactState, notify]);

  useEffect(() => { loadContacts(); }, [loadContacts]);

  const allOnPage = contacts.length > 0 && contacts.every((c) => selected.has(c.id));
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPage) contacts.forEach((c) => next.delete(c.id));
      else contacts.forEach((c) => next.add(c.id));
      return next;
    });
  };
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Soft-deletes leads from the picker. Confirmed first because the row
  // disappears from Lead Finder too, not just from this list.
  const deleteContacts = async (ids) => {
    if (!ids.length || deleting) return;
    const label = ids.length === 1
      ? "Delete this lead? It will be removed from Lead Finder as well."
      : `Delete ${ids.length} leads? They will be removed from Lead Finder as well.`;
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(label)) return;

    setDeleting(true);
    try {
      const res = await authFetch("/whatsapp/contacts/delete", {
        method: "POST",
        body: JSON.stringify({ ids }),
      });
      notify(`Deleted ${res.deleted} lead(s)`, "success");
      // Drop them from the selection so a later send cannot reference a
      // deleted id, then refetch to fix pagination and totals.
      setSelected((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      loadContacts();
    } catch (err) {
      notify(err.message || "Failed to delete leads", "error");
    } finally {
      setDeleting(false);
    }
  };

  const startCampaign = async () => {
    if (!templateKey) { notify("Select an APPROVED template", "error"); return; }
    if (selected.size === 0) { notify("Select at least one contact", "error"); return; }
    if (needsHeaderImage && !headerImageFile) {
      notify("This template has an image header — upload an image first", "error");
      return;
    }
    const [templateName, templateLanguage] = templateKey.split("::");
    setSending(true);
    try {
      const payload = {
        name: campaignName || undefined,
        templateName,
        templateLanguage: templateLanguage || "en",
        leadIds: [...selected],
      };
      const templateParams = {};
      if (bodyParam.trim()) {
        templateParams.body = [bodyParam.trim()];
      }
      if (needsHeaderImage && headerImageFile) {
        const form = new FormData();
        form.append("image", headerImageFile);
        const uploaded = await authFetch("/whatsapp/campaigns/header-image", {
          method: "POST",
          body: form,
        });
        templateParams.header = { type: "image", link: uploaded.link };
      }
      if (Object.keys(templateParams).length) {
        payload.templateParams = templateParams;
      }
      const res = await authFetch("/whatsapp/campaigns", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      notify(`Campaign queued — ${res.enqueued} message(s)`, "success");
      setSelected(new Set());
      setHeaderImageFile(null);
      onCreated?.(res.campaign);
    } catch (err) {
      notify(err.message || "Failed to start campaign", "error");
    } finally {
      setSending(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / 25));

  // The approved template body with {{1}} filled, so the preview shows the
  // real message rather than a placeholder.
  const previewBody = (() => {
    const raw = String(selectedTemplate?.body_text || "");
    if (!raw) return "";
    const fallback = bodyParam.trim() || "there";
    return raw.replace(/\{\{\s*1\s*\}\}/g, fallback);
  })();

  const previewButtons = (() => {
    const comps = selectedTemplate?.components_json;
    if (!comps) return [];
    let parsed = comps;
    if (typeof parsed === "string") {
      try { parsed = JSON.parse(parsed); } catch (_) { return []; }
    }
    const block = (Array.isArray(parsed) ? parsed : []).find(
      (c) => String(c?.type || "").toUpperCase() === "BUTTONS"
    );
    return (block?.buttons || []).map((b) => b.text).filter(Boolean);
  })();

  const hasPreviewContent = Boolean(previewBody) || (needsHeaderImage && !!headerImageUrl);
  const canSend = !sending && selected.size > 0 && !!templateKey
    && (!needsHeaderImage || !!headerImageFile);

  return (
    <div className="wa-ds-layout">
      <div className="wa-ds-form">
        {/* ---------------------------- step 1 ---------------------------- */}
        <section className="wa-step">
          <div className="wa-step-head">
            <span className="wa-step-num">1</span>
            <div>
              <h3>Template &amp; Campaign Details</h3>
              <p>Choose template and set campaign details</p>
            </div>
          </div>

          <div className="wa-step-body wa-ds-grid">
            <label className="wa-field">
              <span>Template</span>
              <select value={templateKey} onChange={(e) => setTemplateKey(e.target.value)}>
                <option value="">Select template…</option>
                {templates.map((t) => (
                  <option key={`${t.name}-${t.language}`} value={`${t.name}::${t.language}`}>
                    {t.name} ({t.language})
                  </option>
                ))}
              </select>
              <small>Select a Meta-approved template</small>
            </label>

            <label className="wa-field">
              <span>Campaign name (optional)</span>
              <input
                type="text"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="October outreach"
              />
              <small>Helps you identify this campaign</small>
            </label>

            <label className="wa-field">
              <span>Name for {"{{1}}"} (optional)</span>
              <input
                type="text"
                value={bodyParam}
                onChange={(e) => setBodyParam(e.target.value)}
                placeholder="Defaults to business name"
              />
              <small>Falls back to each lead's business name</small>
            </label>
          </div>
        </section>

        {/* ---------------------------- step 2 ---------------------------- */}
        {needsHeaderImage && (
          <section className="wa-step">
            <div className="wa-step-head">
              <span className="wa-step-num green">2</span>
              <div>
                <h3>Header image (required by this template)</h3>
                <p>Upload an image that will be sent with the template.</p>
              </div>
            </div>

            <div className="wa-step-body">
              {headerImageFile ? (
                <div className="wa-upload-chosen">
                  <LuImage className="wa-upload-chosen-icon" aria-hidden />
                  <span className="wa-upload-chosen-name" title={headerImageFile.name}>
                    {headerImageFile.name}
                  </span>
                  <span className="wa-upload-chosen-size">
                    {(headerImageFile.size / 1024).toFixed(0)} KB
                  </span>
                  <button
                    type="button"
                    className="wa-upload-remove"
                    onClick={() => setHeaderImageFile(null)}
                    aria-label="Remove image"
                  >
                    <LuX aria-hidden />
                  </button>
                </div>
              ) : (
                <label className="wa-dropzone">
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    onChange={(e) => setHeaderImageFile(e.target.files?.[0] || null)}
                  />
                  <LuUpload className="wa-dropzone-icon" aria-hidden />
                  <strong>Click to upload image</strong>
                  <small>PNG, JPG up to 5 MB</small>
                </label>
              )}
            </div>
          </section>
        )}

        {/* ---------------------------- step 3 ---------------------------- */}
        <section className="wa-step">
          <div className="wa-step-head">
            <span className="wa-step-num blue">{needsHeaderImage ? 3 : 2}</span>
            <div>
              <h3>Select contacts</h3>
              <p>Only leads marked <strong>Qualified</strong> in Lead Finder appear here.</p>
            </div>
            {/* Switching the bucket invalidates the current page and any
                selection made in the other bucket, so both reset. */}
            <div className="wa-state-toggle" role="tablist" aria-label="Contact status">
              {[
                { key: "new", label: "New" },
                { key: "contacted", label: "Sent" },
              ].map((b) => (
                <button
                  key={b.key}
                  type="button"
                  role="tab"
                  aria-selected={contactState === b.key}
                  className={contactState === b.key ? "active" : ""}
                  onClick={() => {
                    if (contactState === b.key) return;
                    setContactState(b.key);
                    setPage(1);
                    setSelected(new Set());
                  }}
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>

          <div className="wa-step-body">
            <div className="wa-filters">
              <input
                className="wa-filter-input"
                placeholder="Search name, phone…"
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
              />
              <input
                className="wa-filter-input narrow"
                placeholder="Keyword"
                value={keyword}
                onChange={(e) => { setKeyword(e.target.value); setPage(1); }}
              />
              <input
                className="wa-filter-input narrow"
                placeholder="Location"
                value={location}
                onChange={(e) => { setLocation(e.target.value); setPage(1); }}
              />
              <span className="wa-select-count">
                {selected.size} selected · {total} {contactState === "new" ? "new" : "contacted"}
              </span>
              {selected.size > 0 && (
                <button
                  type="button"
                  className="wa-bulk-delete"
                  disabled={deleting}
                  onClick={() => deleteContacts([...selected])}
                  title={`Delete ${selected.size} selected lead(s)`}
                >
                  <LuTrash2 /> Delete {selected.size}
                </button>
              )}
            </div>

            <div className="wa-contacts">
              <table className="wa-contacts-table">
                <thead>
                  <tr>
                    <th style={{ width: 34 }}>
                      <input type="checkbox" checked={allOnPage} onChange={toggleAll} />
                    </th>
                    <th>Business / Name</th>
                    <th>Phone</th>
                    <th>Location</th>
                    <th>Last messaged</th>
                    <th style={{ width: 40 }} aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6}><div className="lf-skeleton" /></td></tr>
                  ) : contacts.length === 0 ? (
                    <tr>
                      <td colSpan={6}>
                        <div className="lf-empty">
                          {contactState === "contacted"
                            ? "No contacted numbers yet — nothing has been attempted."
                            : "No new leads left. Qualify more in Lead Finder → Results, or check the Sent tab."}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    contacts.map((c) => (
                      <tr
                        key={c.id}
                        className={selected.has(c.id) ? "selected" : ""}
                        onClick={() => toggleOne(c.id)}
                      >
                        <td onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selected.has(c.id)}
                            onChange={() => toggleOne(c.id)}
                          />
                        </td>
                        <td className="wa-contact-name">
                          {c.business_name || c.name || c.username}
                        </td>
                        <td className="wa-contact-phone">{c.phone}</td>
                        <td>{c.city || c.source_location || "—"}</td>
                        <td>
                          {/* wa_messages dedupes only within one campaign, so a
                              contact messaged by an earlier campaign would
                              otherwise look untouched here. */}
                          {c.contact_state === "failed" ? (
                            <span
                              className="wa-failed-badge"
                              title={`${c.failed_count} attempt(s) failed, latest ${formatFull(c.last_messaged_at)}`}
                            >
                              Failed · {relativeDay(c.last_messaged_at)}
                            </span>
                          ) : c.last_messaged_at ? (
                            <span
                              className="wa-sent-badge"
                              title={`${c.delivered_count} sent${c.failed_count ? `, ${c.failed_count} failed` : ""}, latest ${formatFull(c.last_messaged_at)}`}
                            >
                              {relativeDay(c.last_messaged_at)}
                            </span>
                          ) : (
                            <span className="wa-never-badge">Never</span>
                          )}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="wa-row-delete"
                            disabled={deleting}
                            onClick={() => deleteContacts([c.id])}
                            title="Delete this lead"
                            aria-label={`Delete ${c.business_name || c.name || c.phone}`}
                          >
                            <LuTrash2 />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="wa-pagination">
              <span>Page {page} of {totalPages}</span>
              <div className="wa-page-controls">
                <button
                  className="wa-btn ghost small"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </button>
                <button
                  className="wa-btn ghost small"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </section>

        <button className="wa-btn primary wa-send-btn" onClick={startCampaign} disabled={!canSend}>
          <LuSend aria-hidden />
          {sending
            ? "Starting…"
            : selected.size > 0
              ? `Start Campaign — ${selected.size} contact${selected.size === 1 ? "" : "s"}`
              : "Select contacts to start"}
        </button>
      </div>

      {/* --------------------------- live preview --------------------------- */}
      <aside className="wa-ds-preview">
        <div className={`pc-phone ${device}`}>
          <div className="pc-phone-frame">
            <div className="pc-phone-notch" aria-hidden="true" />

            <div className="pc-phone-topbar">
              <FiArrowLeft aria-hidden size={17} />
              <span className="pc-phone-avatar" aria-hidden="true">C</span>
              <span className="pc-phone-name">Campus 24x7</span>
              <FiVideo aria-hidden size={16} />
              <FiPhoneCall aria-hidden size={15} />
              <FiMoreVertical aria-hidden size={15} />
            </div>

            <div className="pc-phone-chat">
              <div className="pc-phone-day"><span>Today</span></div>

              {hasPreviewContent ? (
                <div className="wa-bubble">
                  {needsHeaderImage && (
                    headerImageUrl
                      ? <img className="wa-bubble-img" src={headerImageUrl} alt="" />
                      : <div className="wa-bubble-img placeholder">Header image</div>
                  )}
                  <p className="wa-bubble-body">{previewBody}</p>
                  <span className="wa-bubble-time">9:41 AM</span>
                  {previewButtons.length > 0 && (
                    <div className="wa-bubble-buttons">
                      {previewButtons.map((b) => (
                        <span className="wa-bubble-button" key={b}>{b}</span>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="pc-phone-placeholder">
                  Your message will appear here as you type.
                </p>
              )}
            </div>

            <div className="pc-phone-composer" aria-hidden="true">
              <span>Message</span>
              <FiChevronRight size={14} />
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

/**
 * Send to numbers pasted by hand — referrals and lists that never came from
 * Lead Finder. Laid out as three numbered steps beside a live phone preview,
 * so what the recipient will actually receive is visible while composing.
 *
 * Preview-before-send is mandatory: a WhatsApp send cannot be recalled, so the
 * user confirms against parsed numbers rather than trusting a silent parse.
 */
function DirectSendTab({ notify, onCreated, templates = [] }) {
  const [templateKey, setTemplateKey] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [bodyParam, setBodyParam] = useState("");
  const [numbers, setNumbers] = useState("");
  const [headerImageFile, setHeaderImageFile] = useState(null);
  const [headerImageUrl, setHeaderImageUrl] = useState("");
  const [preview, setPreview] = useState(null);
  const [checking, setChecking] = useState(false);
  const [sending, setSending] = useState(false);
  // The Android/iPhone toggle is commented out below, so this is fixed for
  // now; `device` still selects the phone frame styling. Restore the setter
  // when the toggle comes back.
  const [device] = useState("android");
  // Shown in the preview's title bar so the mockup reads like the real chat.
  const [senderName] = useState("Campus 24x7");

  const selectedTemplate = templates.find((t) => `${t.name}::${t.language}` === templateKey);
  const needsHeaderImage = String(selectedTemplate?.header_type || "").toUpperCase() === "IMAGE";

  useEffect(() => {
    if (!needsHeaderImage) setHeaderImageFile(null);
  }, [needsHeaderImage]);

  // Object URLs are leaked unless explicitly revoked, and a user may swap the
  // image several times before sending.
  useEffect(() => {
    if (!headerImageFile) { setHeaderImageUrl(""); return undefined; }
    const url = URL.createObjectURL(headerImageFile);
    setHeaderImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [headerImageFile]);

  // `templates` is fetched once by the parent (approved MARKETING only —
  // UTILITY and AUTHENTICATION are transactional and Meta rejects them for
  // promotional sends). This only picks the default once they arrive.
  useEffect(() => {
    if (templates.length && !templateKey) {
      setTemplateKey(`${templates[0].name}::${templates[0].language}`);
    }
  }, [templates, templateKey]);

  // Any edit to the pasted text invalidates the preview, so the confirmed
  // numbers can never drift from what is actually sent.
  const onNumbersChange = (value) => {
    setNumbers(value);
    if (preview) setPreview(null);
  };

  // The approved template body with {{1}} filled in, so the preview shows the
  // real message rather than a placeholder.
  const previewBody = (() => {
    const raw = String(selectedTemplate?.body_text || "");
    if (!raw) return "";
    return raw.replace(/\{\{\s*1\s*\}\}/g, bodyParam.trim() || "there");
  })();

  // Quick-reply / CTA buttons live in the template's own components.
  const previewButtons = (() => {
    const comps = selectedTemplate?.components_json;
    if (!comps) return [];
    let parsed = comps;
    if (typeof parsed === "string") {
      try { parsed = JSON.parse(parsed); } catch (_) { return []; }
    }
    const block = (Array.isArray(parsed) ? parsed : []).find(
      (c) => String(c?.type || "").toUpperCase() === "BUTTONS"
    );
    return (block?.buttons || []).map((b) => b.text).filter(Boolean);
  })();

  // An empty phone shows the idle placeholder rather than an empty bubble.
  const hasPreviewContent = Boolean(previewBody) || (needsHeaderImage && !!headerImageUrl);

  const runPreview = async () => {
    if (!numbers.trim()) { notify("Paste some numbers first", "error"); return; }
    setChecking(true);
    try {
      const res = await authFetch("/whatsapp/campaigns/preview-recipients", {
        method: "POST",
        body: JSON.stringify({ numbers }),
      });
      setPreview(res);
      if (res.counts.valid === 0) notify("No valid numbers found", "error");
    } catch (err) {
      notify(err.message || "Could not check numbers", "error");
    } finally {
      setChecking(false);
    }
  };

  const send = async () => {
    if (!templateKey) { notify("Select an APPROVED template", "error"); return; }
    if (!preview || preview.counts.valid === 0) { notify("Check the numbers first", "error"); return; }
    if (needsHeaderImage && !headerImageFile) {
      notify("This template has an image header — upload an image first", "error");
      return;
    }
    const [templateName, templateLanguage] = templateKey.split("::");
    setSending(true);
    try {
      const payload = {
        name: campaignName || undefined,
        templateName,
        templateLanguage: templateLanguage || "en",
        numbers,
      };
      const templateParams = {};
      if (bodyParam.trim()) templateParams.body = [bodyParam.trim()];
      if (needsHeaderImage && headerImageFile) {
        const form = new FormData();
        form.append("image", headerImageFile);
        const uploaded = await authFetch("/whatsapp/campaigns/header-image", {
          method: "POST",
          body: form,
        });
        templateParams.header = { type: "image", link: uploaded.link };
      }
      if (Object.keys(templateParams).length) payload.templateParams = templateParams;

      const res = await authFetch("/whatsapp/campaigns/direct", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      notify(`Queued ${res.counts.queued} message(s)`, "success");
      setNumbers("");
      setPreview(null);
      setHeaderImageFile(null);
      onCreated?.();
    } catch (err) {
      notify(err.message || "Failed to send", "error");
    } finally {
      setSending(false);
    }
  };

  const canSend = !sending && preview && preview.counts.valid > 0
    && (!needsHeaderImage || !!headerImageFile);

  return (
    <div className="wa-ds-layout">
      <div className="wa-ds-form">
        {/* ---------------------------- step 1 ---------------------------- */}
        <section className="wa-step">
          <div className="wa-step-head">
            <span className="wa-step-num">1</span>
            <div>
              <h3>Template &amp; Campaign Details</h3>
              <p>Choose template and set campaign details</p>
            </div>
          </div>

          <div className="wa-step-body wa-ds-grid">
            <label className="wa-field">
              <span>Template</span>
              <select value={templateKey} onChange={(e) => setTemplateKey(e.target.value)}>
                {templates.length === 0 && <option value="">No approved templates</option>}
                {templates.map((t) => (
                  <option key={`${t.name}::${t.language}`} value={`${t.name}::${t.language}`}>
                    {t.name} ({t.language})
                  </option>
                ))}
              </select>
              <small>Select a Meta-approved template</small>
            </label>

            <label className="wa-field">
              <span>Campaign name (optional)</span>
              <input
                type="text"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="Referral batch"
              />
              <small>Helps you identify this campaign</small>
            </label>

            <label className="wa-field">
              <span>Name for {"{{1}}"} (optional)</span>
              <input
                type="text"
                value={bodyParam}
                onChange={(e) => setBodyParam(e.target.value)}
                placeholder="there"
              />
              <small>Personalize the message (e.g. first name)</small>
            </label>
          </div>
        </section>

        {/* ---------------------------- step 2 ---------------------------- */}
        {needsHeaderImage && (
          <section className="wa-step">
            <div className="wa-step-head">
              <span className="wa-step-num green">2</span>
              <div>
                <h3>Header image (required by this template)</h3>
                <p>Upload an image that will be sent with the template.</p>
              </div>
            </div>

            <div className="wa-step-body">
              {/* No thumbnail here — the phone preview on the right already
                  shows the image in its real context, so a second copy only
                  made this step taller. */}
              {headerImageFile ? (
                <div className="wa-upload-chosen">
                  <LuImage className="wa-upload-chosen-icon" aria-hidden />
                  <span className="wa-upload-chosen-name" title={headerImageFile.name}>
                    {headerImageFile.name}
                  </span>
                  <span className="wa-upload-chosen-size">
                    {(headerImageFile.size / 1024).toFixed(0)} KB
                  </span>
                  <button
                    type="button"
                    className="wa-upload-remove"
                    onClick={() => setHeaderImageFile(null)}
                    aria-label="Remove image"
                  >
                    <LuX aria-hidden />
                  </button>
                </div>
              ) : (
                <label className="wa-dropzone">
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    onChange={(e) => setHeaderImageFile(e.target.files?.[0] || null)}
                  />
                  <LuUpload className="wa-dropzone-icon" aria-hidden />
                  <strong>Click to upload image</strong>
                  <small>PNG, JPG up to 5 MB</small>
                </label>
              )}
            </div>
          </section>
        )}

        {/* ---------------------------- step 3 ---------------------------- */}
        <section className="wa-step">
          <div className="wa-step-head">
            <span className="wa-step-num blue">{needsHeaderImage ? 3 : 2}</span>
            <div>
              <h3>Phone numbers</h3>
              <p>Enter the phone numbers to send this template.</p>
            </div>
          </div>

          <div className="wa-step-body">
            <textarea
              className="wa-numbers-input"
              rows={4}
              value={numbers}
              onChange={(e) => onNumbersChange(e.target.value)}
              placeholder="9876543210, +919812345678, 98111 22233"
            />
            <small className="wa-hint">
              One per line or comma separated — with or without +91
            </small>

            <div className="wa-direct-actions">
              <button className="wa-btn ghost" onClick={runPreview} disabled={checking || !numbers.trim()}>
                <LuListChecks aria-hidden />
                {checking ? "Checking…" : "Check numbers"}
              </button>
            </div>

            {preview && (
              <div className="wa-preview">
                <div className="wa-preview-counts">
                  <span className="wa-pill ok">{preview.counts.valid} valid</span>
                  {preview.counts.duplicates > 0 && (
                    <span className="wa-pill warn">{preview.counts.duplicates} duplicate</span>
                  )}
                  {preview.counts.invalid > 0 && (
                    <span className="wa-pill bad">{preview.counts.invalid} invalid</span>
                  )}
                </div>

                {preview.truncated && (
                  <p className="wa-preview-note">
                    Only the first {preview.maxRecipients} numbers were read.
                  </p>
                )}

                {preview.counts.valid > 0 && (
                  <div className="wa-preview-block">
                    <strong>Will send to</strong>
                    <div className="wa-chip-row">
                      {preview.valid.slice(0, 50).map((v) => (
                        <span className="wa-chip" key={v.phone}>+{v.phone}</span>
                      ))}
                      {preview.valid.length > 50 && (
                        <span className="wa-chip muted">+{preview.valid.length - 50} more</span>
                      )}
                    </div>
                  </div>
                )}

                {preview.duplicates.length > 0 && (
                  <div className="wa-preview-block">
                    <strong>Skipped — already in the list</strong>
                    <div className="wa-chip-row">
                      {preview.duplicates.map((d, i) => (
                        <span className="wa-chip muted" key={`${d.input}-${i}`}>{d.input}</span>
                      ))}
                    </div>
                  </div>
                )}

                {preview.invalid.length > 0 && (
                  <div className="wa-preview-block">
                    <strong>Skipped — not valid</strong>
                    <div className="wa-chip-row">
                      {preview.invalid.map((inv, i) => (
                        <span className="wa-chip bad" key={`${inv.input}-${i}`} title={inv.reason}>
                          {inv.input} — {inv.reason}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        <button className="wa-btn primary wa-send-btn" onClick={send} disabled={!canSend}>
          <LuSend aria-hidden />
          {sending
            ? "Sending…"
            : preview
              ? `Send WhatsApp Messages to ${preview.counts.valid}`
              : "Check numbers first"}
        </button>
      </div>

      {/* --------------------------- live preview --------------------------- */}
      <aside className="wa-ds-preview">
        {/* <div className="wa-preview-head">
          <div className="wa-preview-title">
            <span className="wa-preview-icon"><LuEye aria-hidden /></span>
            <div>
              <h3>Message Preview</h3>
              <p>This is how your message will look to the recipient</p>
            </div>
          </div>
          <div className="wa-device-toggle">
            <button
              type="button"
              className={device === "android" ? "active" : ""}
              onClick={() => setDevice("android")}
            >
              Android
            </button>
            <button
              type="button"
              className={device === "iphone" ? "active" : ""}
              onClick={() => setDevice("iphone")}
            >
              iPhone
            </button>
          </div>
        </div> */}

        <div className={`pc-phone ${device}`}>
          <div className="pc-phone-frame">
            <div className="pc-phone-notch" aria-hidden="true" />

            <div className="pc-phone-topbar">
              <FiArrowLeft aria-hidden size={17} />
              <span className="pc-phone-avatar" aria-hidden="true">
                {(senderName.trim()[0] || "C").toUpperCase()}
              </span>
              <span className="pc-phone-name">{senderName.trim() || "Your Business"}</span>
              <FiVideo aria-hidden size={16} />
              <FiPhoneCall aria-hidden size={15} />
              <FiMoreVertical aria-hidden size={15} />
            </div>

            <div className="pc-phone-chat">
              <div className="pc-phone-day"><span>Today</span></div>

              {hasPreviewContent ? (
                <div className="wa-bubble">
                  {needsHeaderImage && (
                    headerImageUrl
                      ? <img className="wa-bubble-img" src={headerImageUrl} alt="" />
                      : <div className="wa-bubble-img placeholder">Header image</div>
                  )}
                  <p className="wa-bubble-body">{previewBody}</p>
                  <span className="wa-bubble-time">9:41 AM</span>
                  {previewButtons.length > 0 && (
                    <div className="wa-bubble-buttons">
                      {previewButtons.map((b) => (
                        <span className="wa-bubble-button" key={b}>{b}</span>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="pc-phone-placeholder">
                  Your message will appear here as you type.
                </p>
              )}
            </div>

            <div className="pc-phone-composer" aria-hidden="true">
              <span>Message</span>
              <FiChevronRight size={14} />
            </div>
          </div>
        </div>

      </aside>
    </div>
  );
}

function CampaignsLogsTab({ notify, refreshKey }) {
  const [campaigns, setCampaigns] = useState([]);
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [campaignId, setCampaignId] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const loadCampaigns = useCallback(async () => {
    try {
      const res = await authFetch("/whatsapp/campaigns?limit=50");
      setCampaigns(res.campaigns || []);
    } catch (_) { /* ignore */ }
  }, []);

  const loadMessages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`/whatsapp/messages${buildQuery({ page, pageSize: 25, campaignId, status })}`);
      setMessages(res.rows || []);
      setTotal(res.total || 0);
    } catch (err) {
      notify(err.message || "Failed to load messages", "error");
    } finally {
      setLoading(false);
    }
  }, [page, campaignId, status, notify]);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns, refreshKey]);
  useEffect(() => { loadMessages(); }, [loadMessages, refreshKey]);

  // Poll while any campaign is active
  useEffect(() => {
    const active = campaigns.some((c) => ["queued", "running"].includes(c.status));
    if (!active) return undefined;
    const t = setInterval(() => { loadCampaigns(); loadMessages(); }, 4000);
    return () => clearInterval(t);
  }, [campaigns, loadCampaigns, loadMessages]);

  const cancel = async (id) => {
    try {
      await authFetch(`/whatsapp/campaigns/${id}/cancel`, { method: "POST" });
      notify("Campaign cancelled", "info");
      loadCampaigns();
      loadMessages();
    } catch (err) {
      notify(err.message || "Cancel failed", "error");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / 25));

  return (
    <>
      <div className="lf-card">
        <h3 className="lf-card-title">Campaigns</h3>
        {campaigns.length === 0 ? (
          <div className="lf-empty">No campaigns yet.</div>
        ) : (
          <div className="lf-table-wrap">
            <table className="lf-table">
              <thead>
                <tr>
                  <th className="no-sort">Name</th>
                  <th className="no-sort">Template</th>
                  <th className="no-sort">Status</th>
                  <th className="no-sort">Total</th>
                  <th className="no-sort">Sent</th>
                  <th className="no-sort">Delivered</th>
                  <th className="no-sort">Read</th>
                  <th className="no-sort">Failed</th>
                  <th className="no-sort">Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.id} onClick={() => setCampaignId(String(c.id))}>
                    <td className="lf-username">{c.name}</td>
                    <td>{c.template_name} ({c.template_language})</td>
                    <td><span className={`lf-badge ${statusTone(c.status)}`}>{c.status}</span></td>
                    <td>{c.total}</td>
                    <td>{c.sent_count}</td>
                    <td>{c.delivered_count}</td>
                    <td>{c.read_count}</td>
                    <td>{c.failed_count}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {["queued", "running"].includes(c.status) && (
                        <button className="lf-btn lf-btn-danger lf-btn-sm" onClick={() => cancel(c.id)}>
                          <LuX style={{ verticalAlign: "-2px" }} /> Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="lf-card">
        <div className="lf-progress-head">
          <h3 className="lf-card-title" style={{ margin: 0 }}>Delivery Log</h3>
          <div className="lf-toolbar" style={{ margin: 0 }}>
            <select className="lf-select" value={campaignId} onChange={(e) => { setCampaignId(e.target.value); setPage(1); }}>
              <option value="">All campaigns</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <select className="lf-select" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
              <option value="">All statuses</option>
              <option value="queued">Queued</option>
              <option value="sent">Sent</option>
              <option value="delivered">Delivered</option>
              <option value="read">Read</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>

        <div className="lf-table-wrap">
          <table className="lf-table">
            <thead>
              <tr>
                <th className="no-sort">Lead</th>
                <th className="no-sort">Phone</th>
                <th className="no-sort">Template</th>
                <th className="no-sort">Status</th>
                <th className="no-sort">Sent</th>
                <th className="no-sort">Delivered</th>
                <th className="no-sort">Read</th>
                <th className="no-sort">Error</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8}><div className="lf-skeleton" /></td></tr>
              ) : messages.length === 0 ? (
                <tr><td colSpan={8}><div className="lf-empty">No messages yet.</div></td></tr>
              ) : (
                messages.map((m) => (
                  <tr key={m.id}>
                    <td>{m.business_name || m.lead_name || m.lead_username || "—"}</td>
                    <td>{m.recipient}</td>
                    <td>{m.template_name}</td>
                    <td><span className={`lf-badge ${statusTone(m.status)}`}>{m.status}</span></td>
                    <td className="lf-muted wa-log-time">{formatDateTime(m.sent_at)}</td>
                    <td className="lf-muted wa-log-time">{formatDateTime(m.delivered_at)}</td>
                    <td className="lf-muted wa-log-time">{formatDateTime(m.read_at)}</td>
                    <td className="lf-muted wa-body-cell">{m.error_message || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="lf-pagination">
          <span className="lf-page-info">{total} messages · Page {page} of {totalPages}</span>
          <div className="lf-page-controls">
            <button className="lf-btn lf-btn-ghost lf-btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
            <button className="lf-btn lf-btn-ghost lf-btn-sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        </div>
      </div>
    </>
  );
}

function ToastHost({ toasts }) {
  return (
    <div className="lf-toast-wrap">
      {toasts.map((t) => (
        <div key={t.id} className={`lf-toast ${t.type}`}>{t.message}</div>
      ))}
    </div>
  );
}

function WhatsApp() {
  const [tab, setTab] = useState(() => whatsappTabFromHash());
  const [status, setStatus] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [marketingTemplates, setMarketingTemplates] = useState([]);

  const selectTab = (next) => {
    setTab(next);
    setWhatsAppHash(next);
  };

  const notify = useCallback((message, type = "info") => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3200);
  }, []);

  useEffect(() => {
    authFetch("/whatsapp/status")
      .then(setStatus)
      .catch(() => setStatus({ configured: false }));
  }, []);

  // Fetched once here rather than separately inside each compose tab, which
  // previously issued the same request again on every tab switch.
  useEffect(() => {
    authFetch("/whatsapp/templates?approvedOnly=true&category=MARKETING")
      .then((res) => {
        const approved = (res.templates || [])
          .filter((t) => String(t.status).toUpperCase() === "APPROVED");
        setMarketingTemplates(approved);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="lead-finder-page">
      <div className="lf-header">
        <div>
          <h1 className="lf-title">
            WhatsApp Marketings
          </h1>
          <p className="lf-subtitle">
            Send Meta-approved marketing templates to Lead Finder contacts. Delivery status via webhooks.
          </p>
        </div>
        <div className="lf-tabs">
          <button className={`lf-tab ${tab === "campaign" ? "active" : ""}`} onClick={() => selectTab("campaign")}>Campaign</button>
          <button className={`lf-tab ${tab === "direct" ? "active" : ""}`} onClick={() => selectTab("direct")}>Direct Send</button>
          <button className={`lf-tab ${tab === "templates" ? "active" : ""}`} onClick={() => selectTab("templates")}>Templates</button>
          <button className={`lf-tab ${tab === "logs" ? "active" : ""}`} onClick={() => selectTab("logs")}>Campaigns / Logs</button>
        </div>
        <ConnectedPill status={status} />
      </div>

      <ConfigBanner status={status} />

      {/* The two compose tabs stay MOUNTED and are hidden with CSS instead of
          being unmounted. Unmounting made every switch refetch templates (and
          contacts) and discard component state, so returning to Campaign threw
          away the user's selected contacts and left the template dropdown
          empty until the network call finished — which read as lag.
          Templates and Logs still unmount; they hold no in-progress input. */}
      <div hidden={tab !== "campaign"}>
        <CampaignTab
          notify={notify}
          templates={marketingTemplates}
          onCreated={() => { setRefreshKey((k) => k + 1); selectTab("logs"); }}
        />
      </div>
      <div hidden={tab !== "direct"}>
        <DirectSendTab
          notify={notify}
          templates={marketingTemplates}
          onCreated={() => { setRefreshKey((k) => k + 1); selectTab("logs"); }}
        />
      </div>
      {tab === "templates" && <TemplatesTab notify={notify} />}
      {tab === "logs" && <CampaignsLogsTab notify={notify} refreshKey={refreshKey} />}

      <ToastHost toasts={toasts} />
    </div>
  );
}

export default WhatsApp;
